import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IWebexCallHistoryHandle,
  IWebexSpeedDialsHandle,
  IWebexVoicemailHandle,
  WebexCallHistory,
  WebexDialpad,
  WebexSpeedDials,
  WebexVoicemail,
} from '../packages/widgets/src';

import { ButtonCircle, PopoverNext as Popover, TextToast } from '@momentum-ui/react-collaboration';
import { ISpeedDialRecord } from '@webex/component-adapter-interfaces/dist/esm/src';
import { TabBar, TabBarItem, WEBEX_TAB_ID } from '../components/TabBar';
import { TopBar } from '../components/TopBar';
import { PhoneServicesAlert } from '../components/PhoneServicesAlert';
import { useMsTeamsTheme } from "../hooks/useMsTeamsTheme";
import { useWebexUserId } from "../hooks/useWebexUserId";
import { makeCrossLaunchCall } from '../ms-teams-sdk/service/CrossLaunchService';
import { botServiceUserMapping, callPresenceMappingWithRetry, getWebexTokens, getTokenAndRefreshIfNecessary, shouldCallPresenceMappingAgain, WebexTokenEnum } from '../ms-teams-sdk/service/MsTeamsAuthenticationService';
import {
  getLocalStorageItem,
  removeLocalStorageItem
} from '../ms-teams-sdk/util/LocalStorageUtil';
import { getMSTeamsContext } from '../ms-teams-sdk/util/MsTeamsContextUtil';
import {
  CH_STORAGE_KEYS, DISPATCH_STORAGE_EVENTS, IntegrationErrorResponse, SUBSCRIPTION_STATUS, SubscriptionEventResponse, VM_STORAGE_KEYS, WEBEX_STORAGE_KEYS
} from '../packages/adapter-interfaces/src';
import { useLocalStorageNotification } from "../packages/widgets/src/hooks/useLocalStorageNotification";
import { initWebex, whenDeviceRegistered } from "../packages/widgets/src/services/WebexInstanceService";
import { invalidateVoicemailClient } from "../packages/widgets/src/services/WebexVoicemailService";
import { DATA_TRANSFER_ERROR_METRICS_EVENT, DATA_TRANSFER_ERROR_STATUS_FOR_METRICS, DATA_TRANSFER_SUBSCRIPTION_METRICS_EVENT, LOCALIZATION_CODES, SD_SUCCESS_NOTICATION } from "../packages/widgets/src/utils/MetricUtils";
import { CommonErrorAlert } from './Alerts/CommonErrorAlert';
import './LandingPage.scss';
import { callVoicemailURL } from '../ms-teams-sdk/util/TobarUtils';
import { getSelectedLineIndexForCrossLaunch, getSelectedLineGuidForCrossLaunch } from '../components/LineSelector/utils/selectedLineStore';
import { ensureDataPipelineStarted, getDataPipelineSubscription, getErrorEventData, getSubscriptionStatus, openWebexUrlForAckEvent, reNewSubscriptionOnTermination, registerDataPipelineReconnectHandler } from '../packages/widgets/src/services/WebexEventListener';
import { formatError, Logger } from '../packages/logger/src';
import { submitMetrics } from "../packages/widgets/src/services/WebexMetricsService";
import { v4 as uuid } from 'uuid';
import { APP_ENVIRONMENT } from "../packages/widgets/src/utils/environmentTypes";
import { syncContactsForEnrichmentThrottled } from '../services/phoneEnrichment/contactsIntegration';

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const getFocusableElements = (container: HTMLElement | null): HTMLElement[] => {
  if (!container) {
    return [];
  }

  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter((element) => {
    return element.getClientRects().length > 0 && element.tabIndex >= 0 && !element.closest('[aria-hidden="true"]');
  });
};

const resolveFocusableTarget = (target: EventTarget | null): HTMLElement | null => {
  if (!(target instanceof HTMLElement)) {
    return null;
  }

  if (target.matches(FOCUSABLE_SELECTOR)) {
    return target;
  }

  return target.closest<HTMLElement>(FOCUSABLE_SELECTOR);
};

/**
 * Main landing page
 *
 * @returns {JSX.Element} React element containing the tab page
 */
export const LandingPage = (): JSX.Element => {
  const hasCrossLaunchedRef = useRef(false);
  const landingPageRef = useRef<HTMLDivElement>(null);

const fetchClientType = async () => {
  const context = await getMSTeamsContext();
  const webex_orgId = getLocalStorageItem<string>(WebexTokenEnum.ORG_ID);
  if(context?.page?.subPageId && !hasCrossLaunchedRef.current) {
    const botObject = context.page.subPageId.split(" ");
    if(botObject[0] === "BotService" && botObject.length >= 3) {
      hasCrossLaunchedRef.current = true;
      Logger.info(`Deep linking happened via Bot Service: ${context.page.subPageId}`);
      makeCrossLaunchCall(botObject[1], botObject[2].toLowerCase() === 'true');
    }
    else {
      Logger.info(`Deep linking not happened via Bot Service: ${context.page.subPageId}`);
    }
  }
  else {
    Logger.info(`Deep linking failed: ${context?.page?.subPageId}`);
  }};

  useEffect(() => {
    fetchClientType();
  }, [])

const hasMappedRef = useRef(false);
useEffect(() => {
  const init = async () => {
    try {
      if (!hasMappedRef.current) {     
        hasMappedRef.current = true;
        // Run user mapping API calls in parallel for faster initialization
        await Promise.all([
          callPresenceMappingWithRetry(),
          botServiceUserMapping()
        ]);
        Logger.info("Mapping useEffect triggered - parallel execution completed");
      }
    } catch (error) {
      Logger.error(`Initialization error in useEffect: ${error}`);
    }
  };

  init();
  
  // Setup periodic presence mapping refresh (every 10 minutes)
  const intervalId = setInterval(async () => {
    if (shouldCallPresenceMappingAgain()) {
      Logger.info("Re-triggering presence mapping (periodic check)");
      await callPresenceMappingWithRetry();
    }
  }, 10 * 60 * 1000);

  return () => clearInterval(intervalId);
}, []);

useEffect(() => {
  const handleStorageUpdate = (event: StorageEvent) => {
    if (event.key === "presenceMappingLastSuccess") {
      Logger.info("Detected presence mapping update from another tab");
      // Optionally: cancel any pending retry logic if running
    }
  };

  window.addEventListener("storage", handleStorageUpdate);

  return () => {
    window.removeEventListener("storage", handleStorageUpdate);
  };
}, []);

useEffect(() => {
  const handleVisibilityChange = async () => {
    if (document.visibilityState === "visible") {
      try {
        await getTokenAndRefreshIfNecessary(true);
        Logger.info("Revalidated tokens on tab focus.");
      } catch (err) {
        Logger.info("Token refresh failed on tab focus. Might need re-auth.");
      }
    }
  };

  document.addEventListener("visibilitychange", handleVisibilityChange);
  return () => {
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  };
}, []);

useEffect(() => {
  const handleOnline = async () => {
    Logger.info("Reconnected to the app. Validating token...");
    try {
      await getTokenAndRefreshIfNecessary(true);
      await callPresenceMappingWithRetry();
    } catch (error) {
      Logger.info(`Failed to refresh or map after reconnect:, ${error}`);
    }
  };

  window.addEventListener("online", handleOnline);
  return () => {
    window.removeEventListener("online", handleOnline);
  };
}, []);

  const [theme] = useMsTeamsTheme();
  const [unreadMissedCalls, setUnreadMissedCalls] = useState<number>();
  const [unreadVoicemails, setUnreadVoicemails] = useState<number>();
  const [isVmPlayErrorMsg, setIsVmPlayErrorMsg] = useState<boolean>(false);
  const [isAddSpeedDialDisabled, setIsAddSpeedDialDisabled] = useState<boolean>(false);
  const [vmListErrMsg, setVmListErrMsg] = useState<string>('');
  const [chListErrMsg, setChListErrMsg] = useState<string>('');
  const [webexInstErrMsg, setWebexInstErrMsg] = useState<string>('');
  const [isShowVMTab, setIsShowVMTab] = useState<any>(true);
  const [isLocaleGerman, setIsLocaleGerman] = useState<boolean>(false);
  const { t } = useTranslation();
  const callHistoryRef = useRef<IWebexCallHistoryHandle>(null);
  const voicemailRef = useRef<IWebexVoicemailHandle>(null);
  const speedDialsRef = useRef<IWebexSpeedDialsHandle>(null);
  const speedDialAddButtonRef = useRef<HTMLButtonElement>(null);
  const topBarRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLElement>(null);
  const speedDialUserID = 'user1';
  const webexUserId = useWebexUserId();
  const sdSuccessAdd = useLocalStorageNotification(
    SD_SUCCESS_NOTICATION.SD_SUCCESS_ADD,
    2000
  );
  const sdSuccessEdit = useLocalStorageNotification(
    SD_SUCCESS_NOTICATION.SD_SUCCESS_EDIT,
    2000
  );
  const sdSuccessDelete = useLocalStorageNotification(
    SD_SUCCESS_NOTICATION.SD_SUCCESS_DELETE,
    2000
  );
  const sdSuccessReorder = useLocalStorageNotification(
    SD_SUCCESS_NOTICATION.SD_SUCCESS_REORDER,
    2000
  );  
  
  // ---------------------------------------------------------------------
  const MIN_REINIT_INTERVAL_MS = 60_000;
  const reInitInFlightRef = useRef<boolean>(false);
  const lastReInitAtRef = useRef<number>(0);

  type TokenRefreshResult = 'refreshed' | 'unchanged' | 'failed';

  /**
   * Try to refresh the Webex access token through JIS without forcing a
   * full SDK re-init. `getWebexTokens(false)` only triggers a refresh if
   * the access token has actually expired, in which case `saveWebexTokens`
   * will already call `initWebex(...)` for us. When the token is still
   * valid we treat the 401 as a stale-credential issue on a downstream
   * client (e.g. cached `authHeaders` inside the BroadWorks voicemail
   * connector) and leave the SDK alone.
   */
  const tryRefreshWebexToken = async (): Promise<TokenRefreshResult> => {
    try {
      const oldToken = getLocalStorageItem<string>(WebexTokenEnum.AUTH_TOKEN);
      const tokens = await getWebexTokens(false);
      const newToken = tokens?.access_token;
      if (newToken && newToken !== oldToken) {
        Logger.info('LandingPage.tryRefreshWebexToken(): Webex token refreshed via JIS.');
        return 'refreshed';
      }
      Logger.info('LandingPage.tryRefreshWebexToken(): token unchanged (likely stale downstream credentials).');
      return 'unchanged';
    } catch (error) {
      Logger.error(`LandingPage.tryRefreshWebexToken(): failed: ${formatError(error)}`);
      return 'failed';
    }
  };

  const isReInitCoolDownActive = (): boolean => {
    return Date.now() - lastReInitAtRef.current < MIN_REINIT_INTERVAL_MS;
  };

  const reInitWebexInstance = async () => {
    try {
      const webexTokens = await getWebexTokens(false);
      if (!(window?.isWebexInitalized)) {
        initWebex(webexTokens?.access_token as string)
      }
    }
    catch (error) {
      throw error;
    }
  }

  const onHideHandler = () => {
    setIsVmPlayErrorMsg(false)
  };

  const onHideVmListErr = () => {
    setVmListErrMsg('');
    removeLocalStorageItem(VM_STORAGE_KEYS.VM_ERROR_LIST_MSG);
    window.dispatchEvent(new Event("storage"));
  }

  const onHideChListErr = () => {
    setChListErrMsg('');
    removeLocalStorageItem(CH_STORAGE_KEYS.CH_ERROR_LIST_MSG);
    window.dispatchEvent(new Event("storage"));
  }

  const onHideWebexInstErr = () => {
    setWebexInstErrMsg('');
    removeLocalStorageItem(WEBEX_STORAGE_KEYS.WEBEX_INSTANCE_ERROR);
    window.dispatchEvent(new Event("storage"));
  }

  const handleSetIsVmPlayErrorMsg = (newValue: boolean) => {
    setIsVmPlayErrorMsg(newValue);
  }

  const addSpeedDialDisabled = (isDisabled: boolean) => {
    setIsAddSpeedDialDisabled(isDisabled);
  }

  const fetchContextLocale = async () => {
    const context = await getMSTeamsContext();
    if (context?.app?.locale === LOCALIZATION_CODES.DE_DE) {
      setIsLocaleGerman(true)
    }
  };

  const handleRecentCallsTabPress = () => {
    // Fix: don't pile additional requests onto an already-failing system
    // while a re-init is in flight or we are within the post-401 cool-down.
    if (reInitInFlightRef.current) {
      Logger.info('LandingPage.handleRecentCallsTabPress(): re-init in flight, skipping refresh.');
      return;
    }
    if (isReInitCoolDownActive()) {
      Logger.info('LandingPage.handleRecentCallsTabPress(): re-init cool-down active, skipping refresh.');
      return;
    }
    callHistoryRef.current?.refreshCallHistory();
    void syncContactsForEnrichmentThrottled();
  }

  const handleVoicemailTabPress = () => {
    // Fix: same gating as recent calls. `postMissedCallCount` is safe to
    // run even during recovery (it just reads cached state), but the
    // voicemail refresh is what kicks off the XSI calls that 401'd.
    if (reInitInFlightRef.current) {
      Logger.info('LandingPage.handleVoicemailTabPress(): re-init in flight, skipping refresh.');
      callHistoryRef.current?.postMissedCallCount();
      return;
    }
    if (isReInitCoolDownActive()) {
      Logger.info('LandingPage.handleVoicemailTabPress(): re-init cool-down active, skipping refresh.');
      callHistoryRef.current?.postMissedCallCount();
      return;
    }
    voicemailRef.current?.refreshVoicemails();
    callHistoryRef.current?.postMissedCallCount();
  }

  const focusSpeedDialAddButton = () => {
    if (speedDialAddButtonRef.current) {
      speedDialAddButtonRef.current.focus();
      speedDialAddButtonRef.current.classList.add("md-focus-ring-wrapper");
  
      const removeFocusClass = () => {
        speedDialAddButtonRef.current!.classList.remove("md-focus-ring-wrapper");
      };
  
      speedDialAddButtonRef.current.removeEventListener('blur', removeFocusClass);
      speedDialAddButtonRef.current.addEventListener('blur', removeFocusClass);
    }
  };

  // Cross launch to the Webex app for voicemail calls
  const vmCrossLaunchClick = () => {
      const vmUrl = new URL(callVoicemailURL);
      // Send BOTH lineIndex and lineId for Phase 1/2 Desktop compatibility
      const lineIndex = getSelectedLineIndexForCrossLaunch();
      if (lineIndex !== null) {
        vmUrl.searchParams.append('lineIndex', String(lineIndex));
        const lineGuid = getSelectedLineGuidForCrossLaunch();
        if (lineGuid) {
          vmUrl.searchParams.append('lineId', lineGuid);
        }
      }
      window.open(vmUrl.href);
  };

  useEffect(() => {
    const handleLocalStorageChange = (event: StorageEvent) => {
      const stringValue = getLocalStorageItem<string>(VM_STORAGE_KEYS.VM_ERROR_LIST_MSG);
      if (stringValue) {
        setVmListErrMsg(stringValue);
      } else {
        setVmListErrMsg('');
      }
    };
    window.addEventListener('storage', handleLocalStorageChange);

    return () => {
      window.removeEventListener('storage', handleLocalStorageChange);
    };
  }, []);

  useEffect(() => {
    const handleCHERRORLocalStorageChange = (event: StorageEvent) => {
      const stringValue = getLocalStorageItem<string>(CH_STORAGE_KEYS.CH_ERROR_LIST_MSG);
      if (stringValue) {
        setChListErrMsg(stringValue);
      } else {
        setChListErrMsg('');
      }
    };
    window.addEventListener('storage', handleCHERRORLocalStorageChange);

    return () => {
      window.removeEventListener('storage', handleCHERRORLocalStorageChange);
    };
  }, []);

  useEffect(() => {
    const handleWebexInstLocalStorageChange = (event: StorageEvent) => {
      const stringValue = getLocalStorageItem<string>(WEBEX_STORAGE_KEYS.WEBEX_INSTANCE_ERROR);
      if (stringValue) {
        setWebexInstErrMsg(stringValue);
      } else {
        setWebexInstErrMsg('');
      }
    };
    window.addEventListener('storage', handleWebexInstLocalStorageChange);

    return () => {
      window.removeEventListener('storage', handleWebexInstLocalStorageChange);
    };
  }, []);

  useEffect(() => {
    const handleWebexInstUnAuthorizeUser = async (event: StorageEvent) => {
      const isWebexInstanceTokenExpired = getLocalStorageItem<string>(VM_STORAGE_KEYS.WEBEX_INSTANCE_TOKEN_EXPIRED);
      if (!isWebexInstanceTokenExpired) {
        return;
      }
      // Consume the flag eagerly so a duplicate dispatch doesn't queue a
      // second handler invocation while we're working below.
      removeLocalStorageItem(VM_STORAGE_KEYS.WEBEX_INSTANCE_TOKEN_EXPIRED);

      // Fix: in-flight mutex - ignore concurrent 401 dispatches.
      if (reInitInFlightRef.current) {
        Logger.info('LandingPage.handleWebexInstUnAuthorizeUser(): re-init already in flight, skipping.');
        return;
      }
      // Fix: cool-down - bound how often we can re-init the SDK / re-fetch.
      // Bursty 401 storms used to trigger one full re-init per response,
      // which in turn POSTed a new device every couple of seconds and
      // tripped the WDM `/devices` rate limit (429).
      const now = Date.now();
      if (now - lastReInitAtRef.current < MIN_REINIT_INTERVAL_MS) {
        const waitMs = MIN_REINIT_INTERVAL_MS - (now - lastReInitAtRef.current);
        Logger.info(
          `LandingPage.handleWebexInstUnAuthorizeUser(): re-init cool-down active (~${waitMs}ms left), skipping.`
        );
        return;
      }

      reInitInFlightRef.current = true;
      try {
        // Fix: prefer a JIS token refresh (no new device registration)
        // over rebuilding the SDK from scratch. The full re-init path only
        // runs if the refresh attempt failed outright.
        const refreshResult = await tryRefreshWebexToken();
        if (refreshResult === 'failed') {
          Logger.info(
            'LandingPage.handleWebexInstUnAuthorizeUser(): token refresh failed, falling back to full SDK re-init.'
          );
          window.isWebexInitalized = false;
          try {
            await reInitWebexInstance();
          } catch (err) {
            Logger.error(
              `LandingPage.handleWebexInstUnAuthorizeUser(): full re-init failed: ${formatError(err)}`
            );
            return;
          }
        }

        // Fix: drop any cached VoicemailClient so the next request
        // pulls fresh authorization headers from the SDK.
        invalidateVoicemailClient();

        // Wait for the SDK device to be registered (either still the
        // existing one or the one minted by the full-reinit fallback)
        // before re-triggering downstream fetches.
        try {
          await whenDeviceRegistered(20000);
        } catch (err) {
          Logger.info(
            `LandingPage.handleWebexInstUnAuthorizeUser(): whenDeviceRegistered timed out: ${formatError(err)}`
          );
        }

        voicemailRef.current?.refreshVoicemails();
        callHistoryRef.current?.refreshCallHistory();
      } finally {
        // Record the completion timestamp so the cool-down window starts
        // from "we have recovered", not "we started recovering".
        lastReInitAtRef.current = Date.now();
        reInitInFlightRef.current = false;
      }
    };
    window.addEventListener('storage', handleWebexInstUnAuthorizeUser);

    return () => {
      window.removeEventListener('storage', handleWebexInstUnAuthorizeUser);
    };
  }, []);

  useEffect(() => {
    const handleVoicemailTab = (event: StorageEvent) => {
      const isShowVMTabValue = getLocalStorageItem<string>(VM_STORAGE_KEYS.IS_HIDE_VM_TAB);
      if (isShowVMTabValue) {
        setIsShowVMTab(false);
      } else {
        setIsShowVMTab(true);
      }
    };
    window.addEventListener('storage', handleVoicemailTab);

    return () => {
      window.removeEventListener('storage', handleVoicemailTab);
    };
  }, []);

  useEffect(() => {
    fetchContextLocale();
  }, [])

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        await ensureDataPipelineStarted(
          { maxAttempts: 5, baseBackoffMs: 1500 },
          { shouldAbort: () => cancelled }
        );
        if (!cancelled) {
          // Once the initial subscription is established (Mercury is up), arm the
          // reconnect handler so a websocket reconnect renews the (potentially
          // stale) server-side subscription. Without this, integration.settings
          // pushes stop reaching the tab after a reconnect and the Calling Dock
          // toggle freezes on its last value.
          registerDataPipelineReconnectHandler();
          callHistoryRef.current?.refreshCallHistory();
        }
      } catch (error) {
        Logger.error(
          `Error starting data pipeline subscription: '${formatError(error)}'.`
        );
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

// useEffect(() => {
//   const handleVisibilityChange = () => {
//     if (document.visibilityState === "visible") {
//       Logger.info("App is in focus");
//       // Handle logic when the app comes into focus
//     } else {
//       Logger.info("App is not in focus");
//       // Handle logic when the app goes out of focus
//     }
//   };

//   // Add event listener for visibility change
//   document.addEventListener("visibilitychange", handleVisibilityChange);

//   // Cleanup the event listener when the component unmounts
//   return () => {
//     document.removeEventListener("visibilitychange", handleVisibilityChange);
//   };
// }, []);


// data pipeline subscription data
  const handleCheckIntegrationSubscriptionStatus = useCallback(async () => {
    try {
      const response = await getSubscriptionStatus();
      Logger.info(`calling handleCheckIntegrationSubscriptionStatus ${response?.data?.eventType}`)
    } catch (error) {
      Logger.error(`Error fetching subscription service status: '${formatError(error)}'.`);
    }
  }, []); // No dependencies, so it's memoized

  const handleIntegrationSubscriptionStorageEvent = () => {
    // Retrieve the data from local storage
    const subscriptionStorageEventData = getLocalStorageItem<SubscriptionEventResponse>(
      DISPATCH_STORAGE_EVENTS.STORAGE_SUBSCRIPTION_DATA
    );

    if (subscriptionStorageEventData) {
      const subscriptionStatus = subscriptionStorageEventData?.data?.payload?.status;
      Logger.info(
        `Subscription event status: ${subscriptionStatus}`
      );
      // Logic to run when subscriptionEventData is fully populated

      // Optionally call refreshSubscriptionEventData or other functions here
      if (subscriptionStatus === SUBSCRIPTION_STATUS.TERMINATED) {
        Logger.info(
          `Subscription event is terminated`
        );
        const subscriptionId = subscriptionStorageEventData?.data?.subscriptionId || uuid();
        const requestId = subscriptionStorageEventData?.data?.id || uuid();
        reNewSubscriptionOnTermination(subscriptionId, requestId);
        submitMetrics(
          {
            metricEvent: DATA_TRANSFER_SUBSCRIPTION_METRICS_EVENT,
            dataTransferSubscriptionStatus: SUBSCRIPTION_STATUS.TERMINATED
          }
        );
      } else if(subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE) {
        Logger.info(
          `Subscription event is active`
        );
        submitMetrics(
          {
            metricEvent: DATA_TRANSFER_SUBSCRIPTION_METRICS_EVENT,
            dataTransferSubscriptionStatus: SUBSCRIPTION_STATUS.ACTIVE
          }
        );
      }
    }

  }

  useEffect(() => {
    // Fetch initial data from localStorage
    handleIntegrationSubscriptionStorageEvent();

    // Add event listener for the storage event
    window.addEventListener('localstorage:subscription', handleIntegrationSubscriptionStorageEvent);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('localstorage:subscription', handleIntegrationSubscriptionStorageEvent);
    };
  }, []);

  useEffect(() => {
    handleCheckIntegrationSubscriptionStatus();
  }, [handleCheckIntegrationSubscriptionStatus]);


//error subscription
  const handleCheckIntegrationErrorStatus = useCallback(async () => {
    try {
      const response = await getErrorEventData();
      Logger.info(`calling handleCheckIntegrationErrorStatus ${response?.data?.eventType}`)
    } catch (error) {
      Logger.error(`Error subscription status: '${formatError(error)}'.`);
    }
  }, []); // No dependencies, so it's memoized

  // const handleIntegrationErrorStorageEvent = () => {
  //   // Retrieve the data from local storage
  //   const errorEventData = getLocalStorageItem<IntegrationErrorResponse>(
  //     DISPATCH_STORAGE_EVENTS.STORAGE_INTEGRATION_ERROR
  //   );
  //   if (errorEventData) {
  //     if (
  //       errorEventData?.data?.payload?.error

  //     ) {

  //       const errorCode = errorEventData?.data?.payload?.error;
  //       Logger.info(
  //         `Error subscription event description:', ${errorEventData?.data?.payload?.description}`
  //       );

  //       Logger.info(
  //         `Error subscription event error code:', ${errorCode}`
  //       );

  //       // Optionally call refreshSubscriptionEventData or other functions here

  //       Logger.info(`Got an error, initiating subscription method.`);
  //       const fetchDataSubscription = async () => {
  //         try {
  //           await getDataPipelineSubscription();
  //         } catch (error) {
  //           console.error("Error in useEffect while getting data pipeline subscription:", error);
  //         }
  //       };

  //       fetchDataSubscription();

  //       if(errorCode === '1001') {
  //         submitMetrics(
  //           {
  //             metricEvent: DATA_TRANSFER_ERROR_METRICS_EVENT,
  //             dataTransferErrorStatus: DATA_TRANSFER_ERROR_STATUS_FOR_METRICS.INVALID_TOKEN
               
  //           }
  //         );
  //       } else if(errorCode === '1000') {
  //         submitMetrics(
  //           {
  //             metricEvent: DATA_TRANSFER_ERROR_METRICS_EVENT,
  //             dataTransferErrorStatus: DATA_TRANSFER_ERROR_STATUS_FOR_METRICS.NOT_SUPPORTED
               
  //           }
  //         );
  //       } else if(errorCode === '1002') {
  //         submitMetrics(
  //           {
  //             metricEvent: DATA_TRANSFER_ERROR_METRICS_EVENT,
  //             dataTransferErrorStatus: DATA_TRANSFER_ERROR_STATUS_FOR_METRICS.INVALID_SUBSCRIPTION
               
  //           }
  //         );
  //       }


  //     }
  //   }
  // }

  const handleIntegrationErrorStorageEvent = () => {
    // Retrieve the data from local storage
    const errorEventData = getLocalStorageItem<IntegrationErrorResponse>(
      DISPATCH_STORAGE_EVENTS.STORAGE_INTEGRATION_ERROR
    );

    // Safely destructure with default values
    const {
      error: errorCode = '',
      description = 'No description available'
    } = errorEventData?.data?.payload || {};

    // Check if there's no errorCode; exit early
    if (!errorCode) {
      Logger.info("No error code found in the payload. Exiting function.");
      return;
    }
    const errorEventId = errorEventData?.data?.id || uuid();
    openWebexUrlForAckEvent(errorEventId, `Errorcode ${errorCode} - ${description}`);
    // Log error details
    Logger.info(`Error subscription event description: ${description}`);
    Logger.info(`Error subscription event error code: ${errorCode}`);

    // Handle the subscription data refresh
    const refreshSubscriptionData = async () => {
      try {
        await getDataPipelineSubscription();
        Logger.info("Data pipeline subscription refreshed successfully.");
      } catch (error) {
        console.error("Error while refreshing data pipeline subscription:", JSON.stringify(error));
      }
    };

    // Call the refresh function
    refreshSubscriptionData();


    // Map error codes to metric statuses
    const metricStatusMapping = {
      '1001': DATA_TRANSFER_ERROR_STATUS_FOR_METRICS.INVALID_TOKEN,
      '1000': DATA_TRANSFER_ERROR_STATUS_FOR_METRICS.NOT_SUPPORTED,
      '1002': DATA_TRANSFER_ERROR_STATUS_FOR_METRICS.INVALID_SUBSCRIPTION,
    };

    type ErrorCode = keyof typeof metricStatusMapping;

    if (errorCode in metricStatusMapping) {
      submitMetrics({
        metricEvent: DATA_TRANSFER_ERROR_METRICS_EVENT,
        dataTransferErrorStatus: metricStatusMapping[errorCode as ErrorCode],
      });
      Logger.info(`Submitted metrics for error code ${errorCode}.`);
    } else {
      Logger.info(`Unhandled error code: ${errorCode}`);
    }
  };


  useEffect(() => {
    // Fetch initial data from localStorage
    handleIntegrationErrorStorageEvent();

    // Add event listener for the storage event
    window.addEventListener('localstorage:integrationError', handleIntegrationErrorStorageEvent);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('localstorage:integrationError', handleIntegrationErrorStorageEvent);
    };
  }, []);


  useEffect(() => {
    handleCheckIntegrationErrorStatus();
  }, [handleCheckIntegrationErrorStatus]);

  const focusTopBarFromContentEnd = useCallback(() => {
    const nextToolbarControl = getFocusableElements(topBarRef.current)[0];
    if (nextToolbarControl) {
      requestAnimationFrame(() => {
        nextToolbarControl.focus();
      });
    }
  }, []);

  const focusTopBarEntry = useCallback(() => {
    const nextToolbarControl = getFocusableElements(topBarRef.current)[0];
    if (nextToolbarControl) {
      requestAnimationFrame(() => {
        nextToolbarControl.focus();
      });
    }
  }, []);

  useEffect(() => {
    const handleDocumentTabOrder = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) {
        return;
      }

      const landingPage = landingPageRef.current;
      if (!landingPage) {
        return;
      }

      const activeElement = resolveFocusableTarget(document.activeElement);
      if (!activeElement || !landingPage.contains(activeElement)) {
        return;
      }

      const visibleToolbarFocusables = getFocusableElements(topBarRef.current);
      const visibleContentFocusables = getFocusableElements(contentRef.current);
      const orderedFocusables = [...visibleToolbarFocusables, ...visibleContentFocusables];

      if (orderedFocusables.length < 2) {
        return;
      }

      const currentIndex = orderedFocusables.findIndex((element) => element === activeElement);
      if (currentIndex === -1) {
        return;
      }

      const nextIndex = event.shiftKey
        ? (currentIndex - 1 + orderedFocusables.length) % orderedFocusables.length
        : (currentIndex + 1) % orderedFocusables.length;

      const nextFocusable = orderedFocusables[nextIndex];
      if (!nextFocusable || nextFocusable === activeElement) {
        return;
      }

      event.preventDefault();
      nextFocusable.focus();
    };

    document.addEventListener('keydown', handleDocumentTabOrder, true);
    return () => {
      document.removeEventListener('keydown', handleDocumentTabOrder, true);
    };
  }, []);

  return (
    <div
      ref={landingPageRef}
      className="landing-page"
    >
      <div
        tabIndex={0}
        className="landing-page__focus-sentinel"
        onFocus={focusTopBarEntry}
      />
      <header className="landing-page__header">
      <PhoneServicesAlert />
        <div ref={topBarRef}>
          <TopBar />
        </div>
      </header>
      <section ref={contentRef} className="landing-page__content">
        <TabBar>
          <TabBarItem tabId={WEBEX_TAB_ID.DIALPAD} label={t('dialpad')}>
            <WebexDialpad theme={theme} />
          </TabBarItem>

          <TabBarItem
            tabId={WEBEX_TAB_ID.SPEED_DIAL}
            label={t('speedDial')}
            onActiveRightItem={
              <div className='tooltip-wrapper'>
                {!isAddSpeedDialDisabled &&
                  <Popover
                    color="primary"
                    delay={[0, 0]}
                    placement="bottom"
                    showArrow={false}
                    trigger="mouseenter"
                    className="tooltip"
                    triggerComponent={
                      <ButtonCircle
                        aria-label={t('label.speed_dial.add')}
                        ghost
                        onPress={() => {
                          speedDialsRef.current?.showSearchSpeedDials();
                        }}
                        size={28}
                        ref={speedDialAddButtonRef}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 12 12" fill="none">
                          <path d="M11 5.5H6.5V1C6.5 0.867392 6.44732 0.740215 6.35355 0.646446C6.25979 0.552678 6.13261 0.5 6 0.5C5.86739 0.5 5.74021 0.552678 5.64645 0.646446C5.55268 0.740215 5.5 0.867392 5.5 1V5.5H1C0.867392 5.5 0.740215 5.55268 0.646447 5.64645C0.552679 5.74021 0.5 5.86739 0.5 6C0.5 6.13261 0.552679 6.25979 0.646447 6.35355C0.740215 6.44732 0.867392 6.5 1 6.5H5.5V11C5.5 11.1326 5.55268 11.2598 5.64645 11.3536C5.74021 11.4473 5.86739 11.5 6 11.5C6.13261 11.5 6.25979 11.4473 6.35355 11.3536C6.44732 11.2598 6.5 11.1326 6.5 11V6.5H11C11.1326 6.5 11.2598 6.44732 11.3536 6.35355C11.4473 6.25979 11.5 6.13261 11.5 6C11.5 5.86739 11.4473 5.74021 11.3536 5.64645C11.2598 5.55268 11.1326 5.5 11 5.5Z" fill="var(--mds-color-theme-text-primary-normal)" fillOpacity="0.95" />
                        </svg>
                      </ButtonCircle>
                    }
                    variant="small"
                  >
                    <p>{t('label.speed_dial.add')}</p>
                  </Popover>
                }

                {isAddSpeedDialDisabled &&
                  <Popover
                    color="primary"
                    delay={[0, 0]}
                    placement="bottom"
                    showArrow={false}
                    trigger="mouseenter"
                    className="tooltip"
                    triggerComponent={
                      <ButtonCircle
                        className="btn-disabled"
                        onPress={() => {
                        }}
                        size={28}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 12 12" fill="none">
                          <path d="M11 5.75H6.25V1C6.25 0.933696 6.22366 0.870107 6.17678 0.823223C6.12989 0.776339 6.0663 0.75 6 0.75C5.9337 0.75 5.87011 0.776339 5.82322 0.823223C5.77634 0.870107 5.75 0.933696 5.75 1V5.75H1C0.933696 5.75 0.870107 5.77634 0.823223 5.82322C0.776339 5.87011 0.75 5.9337 0.75 6C0.75 6.0663 0.776339 6.12989 0.823223 6.17678C0.870107 6.22366 0.933696 6.25 1 6.25H5.75V11C5.75 11.0663 5.77634 11.1299 5.82322 11.1768C5.87011 11.2237 5.9337 11.25 6 11.25C6.0663 11.25 6.12989 11.2237 6.17678 11.1768C6.22366 11.1299 6.25 11.0663 6.25 11V6.25H11C11.0663 6.25 11.1299 6.22366 11.1768 6.17678C11.2237 6.12989 11.25 6.0663 11.25 6C11.25 5.9337 11.2237 5.87011 11.1768 5.82322C11.1299 5.77634 11.0663 5.75 11 5.75Z" fill="var(--mds-color-theme-text-primary-normal)" fillOpacity="0.4" />
                        </svg>
                      </ButtonCircle>
                    }
                    variant="small"
                  >
                    <p>{t('label.speed_dial.max_reached')}</p>
                  </Popover>
                }
              </div>
            }
          >
            <WebexSpeedDials
              onPress={(item: ISpeedDialRecord) => {
                makeCrossLaunchCall(
                  item?.currentCallAddress as string,
                  item.callType === 'handset'
                );
              }}
              onAddPress={() => {
                speedDialsRef.current?.showSearchSpeedDials();
              }}
              onAudioCallPress={(item: ISpeedDialRecord) => {
                makeCrossLaunchCall(
                  item?.currentCallAddress as string,
                  true
                );
              }}
              onVideoCallPress={(item: ISpeedDialRecord) => {
                makeCrossLaunchCall(
                  item?.currentCallAddress as string,
                  false
                );
              }}
              userID={speedDialUserID}
              ref={speedDialsRef}
              contacts={[]}
              addSpeedDialDisabled={addSpeedDialDisabled}
              focusSpeedDialAddButton={focusSpeedDialAddButton}
              theme={theme}
            />
          </TabBarItem>
        </TabBar>
        <div className='landing-page__wrapperVMCH'>
          {webexInstErrMsg &&
            <div className='landing-page__bannerVM'>
              <CommonErrorAlert
                errorMessage={t('errorWebexInst')}
                onClose={onHideWebexInstErr}
              />
            </div>
          }
          {isVmPlayErrorMsg &&
            <div className='landing-page__bannerVM'>
              <CommonErrorAlert
                errorMessage={t('vmPlayErrorMessage')}
                onClose={onHideHandler}
              />
            </div>
          }
          {vmListErrMsg &&
            <div className='landing-page__bannerVM'>
              <CommonErrorAlert
                errorMessage={t('errorListVM')}
                onClose={onHideVmListErr}
              />
            </div>}
          {chListErrMsg &&
            <div className='landing-page__bannerVM'>
              <CommonErrorAlert
                errorMessage={t('errorListCH')}
                onClose={onHideChListErr}
              />
            </div>
          }
          <TabBar>
            <TabBarItem
              tabId={WEBEX_TAB_ID.RECENT_CALLS}
              label={t('recentCalls')}
              badgeCount={unreadMissedCalls}
              onTabPress={handleRecentCallsTabPress}
            >
              {webexUserId ? (
                <WebexCallHistory
                  key={webexUserId}
                  noHistoryMessage={t('calls.no_calling_history')}
                  userID={webexUserId}
                  ref={callHistoryRef}
                  isLocaleGerman={isLocaleGerman}
                  updateMissedCallCount={(unreadCount: number) =>
                    setUnreadMissedCalls(unreadCount)
                  }
                  labelUnknown={t("calls.call_with_NoName_NoAddress") || "Unknown"}
                />
              ) : null}
            </TabBarItem>
            {isShowVMTab &&
              <TabBarItem
                tabId={WEBEX_TAB_ID.VOICEMAIL}
                label={t('voicemail')}
                badgeCount={unreadVoicemails}
                onTabPress={handleVoicemailTabPress}
                onActiveRightItem = {
                  // Voicemail cross launch icon display
                  <ButtonCircle size={40} ghost key="1" aria-label={t('callVoicemail', { fallback: 'Call Voicemail' })} onPress={vmCrossLaunchClick}>
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16.3893 10.0001H19.6104C19.8852 10.0001 20.1538 9.91865 20.3822 9.76601C20.6107 9.61336 20.7887 9.3964 20.8939 9.14257C20.999 8.88873 21.0265 8.60942 20.9729 8.33995C20.9193 8.07048 20.787 7.82295 20.5927 7.62867C20.3985 7.4344 20.1509 7.30209 19.8815 7.24849C19.612 7.19489 19.3327 7.22241 19.0788 7.32755C18.825 7.43269 18.608 7.61075 18.4554 7.83919C18.3028 8.06764 18.2213 8.33622 18.2213 8.61097C18.2232 8.74302 18.2442 8.87409 18.2836 9.00013H17.7161C17.7555 8.87409 17.7765 8.74302 17.7784 8.61097C17.7784 8.33622 17.697 8.06764 17.5443 7.83919C17.3917 7.61075 17.1747 7.43269 16.9209 7.32755C16.667 7.22241 16.3877 7.19489 16.1183 7.24849C15.8488 7.30209 15.6013 7.4344 15.407 7.62867C15.2127 7.82295 15.0804 8.07047 15.0268 8.33994C14.9732 8.60941 15.0007 8.88873 15.1058 9.14257C15.211 9.3964 15.389 9.61336 15.6175 9.766C15.8459 9.91865 16.1145 10.0001 16.3893 10.0001ZM19.2213 8.61097C19.2212 8.53393 19.244 8.45861 19.2868 8.39454C19.3296 8.33046 19.3904 8.28051 19.4616 8.25101C19.5327 8.22151 19.611 8.21378 19.6866 8.22879C19.7622 8.24381 19.8316 8.2809 19.886 8.33538C19.9405 8.38985 19.9776 8.45926 19.9926 8.53482C20.0076 8.61038 19.9999 8.68869 19.9704 8.75986C19.9409 8.83102 19.891 8.89184 19.8269 8.93461C19.7628 8.97738 19.6875 9.00018 19.6104 9.00013C19.5073 8.99994 19.4084 8.95888 19.3355 8.88594C19.2625 8.813 19.2215 8.71412 19.2213 8.61097ZM16.3893 8.22181C16.4662 8.22181 16.5415 8.24464 16.6055 8.2874C16.6695 8.33016 16.7193 8.39094 16.7488 8.46205C16.7783 8.53316 16.786 8.6114 16.7709 8.68689C16.7559 8.76238 16.7189 8.83173 16.6644 8.88615C16.61 8.94058 16.5407 8.97764 16.4652 8.99265C16.3897 9.00767 16.3114 8.99996 16.2403 8.97051C16.1692 8.94105 16.1084 8.89118 16.0657 8.82718C16.0229 8.76318 16.0001 8.68794 16.0001 8.61097C16.0003 8.50782 16.0414 8.40894 16.1143 8.336C16.1872 8.26306 16.2861 8.22201 16.3893 8.22181Z" fill="var(--mds-color-theme-text-primary-normal)" fill-opacity="0.95"/>
                <path d="M20.5001 11.0001H15.5001C15.3675 11.0001 15.2403 11.0528 15.1465 11.1466C15.0528 11.2403 15.0001 11.3675 15.0001 11.5001C15.0001 11.6327 15.0528 11.7599 15.1465 11.8537C15.2403 11.9474 15.3675 12.0001 15.5001 12.0001H20.5001C20.6327 12.0001 20.7599 11.9474 20.8537 11.8537C20.9474 11.7599 21.0001 11.6327 21.0001 11.5001C21.0001 11.3675 20.9474 11.2403 20.8537 11.1466C20.7599 11.0528 20.6327 11.0001 20.5001 11.0001Z" fill="var(--mds-color-theme-text-primary-normal)" fill-opacity="0.95"/>
                <path d="M10.5536 17.4213C14.0515 20.9192 17.1854 20.6889 18.0859 19.7885L18.6045 19.2699C18.9068 18.9669 19.0766 18.5564 19.0766 18.1284C19.0766 17.7004 18.9068 17.2899 18.6045 16.987L17.3425 15.725C17.0405 15.4252 16.6318 15.2575 16.2063 15.2586C15.7807 15.2597 15.3729 15.4295 15.0724 15.7309L14.4937 16.3096C13.7991 16.1653 13.1647 15.8132 12.6749 15.3C12.1619 14.8104 11.8093 14.1769 11.6636 13.4829L12.2474 12.8997C12.543 12.5845 12.7038 12.1665 12.6958 11.7345C12.6877 11.3025 12.5114 10.8907 12.2043 10.5868L10.9879 9.37039C10.685 9.06809 10.2744 8.89832 9.84647 8.89832C9.41848 8.89832 9.00798 9.06809 8.70501 9.37039L8.18641 9.88899C7.28593 10.7894 7.05565 13.9234 10.5536 17.4213ZM9.4121 10.0775C9.52736 9.9624 9.68358 9.89776 9.84645 9.89776C10.0093 9.89776 10.1655 9.9624 10.2808 10.0775L11.4972 11.2939C11.6179 11.4097 11.6897 11.5673 11.6978 11.7344C11.7058 11.9015 11.6496 12.0653 11.5407 12.1922L10.8494 12.8828C10.3091 13.4231 11.0127 15.0521 11.9677 16.0071C13.0184 17.0577 14.5963 17.6212 15.0941 17.1233L15.7795 16.438C15.8923 16.3239 16.0458 16.2592 16.2063 16.2581C16.3667 16.257 16.5211 16.3196 16.6354 16.4321L17.8974 17.6941C18.0125 17.8093 18.0771 17.9655 18.0771 18.1284C18.0771 18.2913 18.0125 18.4475 17.8974 18.5628L17.3788 19.0814C16.845 19.6151 14.2413 19.6949 11.2606 16.7142C8.27996 13.7335 8.35971 11.1299 8.8935 10.5961L9.4121 10.0775Z" fill="var(--mds-color-theme-text-primary-normal)" fill-opacity="0.95"/>
              </svg>
                </ButtonCircle>
                        /* Voicemail unread badge counter display if there are multilines*/
                    //  { (unreadVoicemails || 0) >= 1 &&
                    //   <div className='unread-bubble' role="img" aria-label={t('unreadVoicemail')} />
                    // }} 
                }
              >
                {webexUserId ? (
                  <WebexVoicemail
                    key={webexUserId}
                    handleSetIsVmPlayErrorMsg={handleSetIsVmPlayErrorMsg}
                    updateUnreadCount={(unreadCount: number) =>
                      setUnreadVoicemails(unreadCount)
                    }
                    ref={voicemailRef}
                    isLocaleGerman={isLocaleGerman}
                    userID={webexUserId}
                  />
                ) : null}
              </TabBarItem>
            }
          </TabBar>
        </div>
      </section>
      <div
        tabIndex={0}
        className="landing-page__focus-sentinel"
        onFocus={focusTopBarFromContentEnd}
      />
      <section role="status">
        {sdSuccessAdd && (
          <TextToast
            className='tostCenter'
            text={t('successAdd')}
            aria-live={t('successAdd')}
          />
        )}
        {sdSuccessDelete && (
          <TextToast
            className='tostCenter'
            text={t('successDelete')}
            aria-live={t('successDelete')}
          />
        )}
        {sdSuccessEdit && (
          <TextToast
            className='tostCenter'
            text={t('successEdit')}
            aria-live={t('successEdit')}
          />
        )}
        {sdSuccessReorder && (
          <TextToast
            className='tostCenter'
            text={t('successReorder')}
            aria-live={t('successReorder')}
          />
        )}
      </section>
    </div>
  );
};
